import { useState, useEffect } from "react";
import { fetchProjectsApi } from "../../services/api/boq/projectService";
import { fetchClientsApi } from "../../services/api/boq/clientService";
import { fetchBoqByProject } from "../../services/api/boq/boqService";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";


// Utility for BOQ status colors
const statusColor = (status) => {
  switch (status) {
    case "approved":
      return "bg-green-100 text-green-700";
    case "draft":
      return "bg-yellow-100 text-yellow-700";
    case "rejected":
      return "bg-red-100 text-red-700";
    default:
      return "";
  }
};

// Reusable summary card
const StatCard = ({ title, value, colorClass = "bg-white" }) => (
  <div className={`p-4 rounded-xl shadow text-center ${colorClass}`}>
    <p className="text-gray-500 text-sm">{title}</p>
    <p className="text-2xl font-bold">{value}</p>
  </div>
);

export default function AdminDashboard() {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [boqs, setBoqs] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [boqLoading, setBoqLoading] = useState(false);

  const [totalProjectsCount, setTotalProjectsCount] = useState(0);
  const [totalClientsCount, setTotalClientsCount] = useState(0);



  // Load dashboard data
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        const projectsRes = await fetchProjectsApi();
        const clientsRes = await fetchClientsApi();

        // Paginated projects
        const projectsArray = projectsRes.data.data.data || [];
        setProjects(projectsArray);
        setTotalProjectsCount(projectsRes.data.data.total || projectsArray.length);

        // Clients
        const clientsArray = clientsRes.data || [];
        setClients(clientsArray);
        setTotalClientsCount(clientsRes.total || clientsArray.length);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  useEffect(() => {
    if (projects.length > 0) {
      handleViewBoqs(projects[0]);
    }
  }, [projects]);

  // Fetch BOQs by project (IMPORTANT FIX)
  const handleViewBoqs = async (project) => {
    try {
      setSelectedProject(project);
      setBoqLoading(true);
      setBoqs([]);

      const res = await fetchBoqByProject(project.id);

      let boqArray = [];

      if (Array.isArray(res)) {
        boqArray = res;
      } else if (res?.boq) {
        boqArray = [res.boq];
      }

      setBoqs(boqArray);

    } catch (err) {
      console.error("Failed to load BOQs", err);
    } finally {
      setBoqLoading(false);
    }
  };


  // Derived metrics
  const totalBoqs = boqs.length;
  const totalBoqValue = boqs.reduce(
    (sum, b) => sum + Number(b.total_amount || 0),
    0
  );

  const recentProjects = [...projects]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 5);

  if (loading)
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );

  const boqStatusCount = boqs.reduce(
    (acc, b) => {
      switch (b.status) {
        case "draft":
          acc.Draft++;
          break;
        case "approved":
          acc.Approved++;
          break;
        case "rejected":
          acc.Rejected++;
          break;
        default:
          acc.Draft++;
      }
      return acc;
    },
    { Draft: 0, Approved: 0, Rejected: 0 }
  );

  const boqChartData = [
    { name: "Draft", value: boqStatusCount.Draft },
    { name: "Approved", value: boqStatusCount.Approved },
    { name: "Rejected", value: boqStatusCount.Rejected },
  ];


  const projectStatusCount = projects.reduce(
    (acc, p) => {
      switch (p.status) {
        case "active":
          acc.Active++;
          break;
        case "completed":
          acc.Completed++;
          break;
        case "on-hold":
          acc["On Hold"]++;
          break;
        default:
          acc.Active++;
      }
      return acc;
    },
    { Active: 0, Completed: 0, "On Hold": 0 }
  );

  const projectChartData = [
    { name: "Active", value: projectStatusCount.Active },
    { name: "Completed", value: projectStatusCount.Completed },
    { name: "On-Hold", value: projectStatusCount["On Hold"] },
  ];

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard title="Projects" value={totalProjectsCount} colorClass="bg-indigo-50" />
          <StatCard title="Clients" value={totalClientsCount} colorClass="bg-purple-50" />
          <StatCard title="BOQs" value={totalBoqs} colorClass="bg-blue-50" />
          <StatCard
            title="BOQ Value"
            value={`₹ ${totalBoqValue.toLocaleString()}`}
            colorClass="bg-green-50"
          />
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* Project Status Chart */}
          <div className="bg-white rounded-xl shadow p-4 h-72 inline-flex">
            <h2 className="text-lg font-semibold mb-2">Project Status</h2>

            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={projectChartData}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={80}
                  label
                >
                  <Cell fill="#3B82F6" /> {/* Active */}
                  <Cell fill="#22C55E" /> {/* Completed */}
                  <Cell fill="#FACC15" /> {/* On Hold */}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* BOQ Status Chart */}
          <div className="bg-white rounded-xl shadow p-4 h-72 flex">
            <h2 className="text-lg font-semibold mb-2">BOQ Status</h2>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={boqChartData}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={80}
                  label
                >
                  <Cell fill="#16A34A" /> {/* Approved */}
                  <Cell fill="#FACC15" /> {/* Pending */}
                  <Cell fill="#DC2626" /> {/* Rejected */}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>

        </div>


        {/* Projects + BOQs */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[60vh]">

          {/* Projects */}
          <div className="bg-white rounded-xl shadow p-4 h-full overflow-y-auto sticky top-4">
            <h2 className="text-lg font-semibold mb-4">Projects</h2>

            <div className="space-y-3">
              {projects.map((p) => (
                <div
                  key={p.id}
                  className={`p-3 border rounded-lg flex justify-between items-center ${selectedProject?.id === p.id ? "bg-blue-50 border-blue-400" : ""
                    }`}
                >
                  <span className="font-medium">{p.project_name}</span>
                  <button
                    onClick={() => handleViewBoqs(p)}
                    className="text-blue-600 text-sm"
                  >
                    View BOQs →
                  </button>
                </div>
              ))}
            </div>
          </div>


          {/* BOQ Side Panel */}
          {selectedProject && (
            <div className="lg:col-span-2 bg-white rounded-xl shadow p-4 h-full overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">
                  BOQs – {selectedProject.project_name}
                </h2>
                <button onClick={() => setSelectedProject(null)}>✕</button>
              </div>

              {boqLoading ? (
                <div className="flex justify-center py-10">
                  <div className="w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : boqs.length === 0 ? (
                <p className="text-center text-gray-500">No BOQs found</p>
              ) : (
                <table className="w-full border">
                  <thead className="bg-green-600 text-white">
                    <tr>
                      <th className="p-2 text-left">BOQ</th>
                      <th className="p-2">Status</th>
                      <th className="p-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {boqs.map((boq) => (
                      <tr key={boq.id} className="border-b">
                        <td className="p-2">{boq.boq_name}</td>
                        <td className="p-2">
                          <span className={`px-2 py-1 rounded ${statusColor(boq.status)}`}>
                            {boq.status}
                          </span>
                        </td>
                        <td className="p-2 text-right">
                          ₹ {Number(boq.total_amount || 0).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        {/* Recent Projects */}
        <div>
          <h2 className="text-lg font-semibold mb-2">Recent Projects</h2>
          <ul className="space-y-2">
            {recentProjects.map((p) => (
              <li key={p.id} className="bg-white p-3 rounded shadow flex justify-between">
                <span>{p.project_name}</span>
                <span className="text-gray-400 text-sm">
                  {new Date(p.created_at).toLocaleDateString()}
                </span>
              </li>
            ))}
          </ul>
        </div>

      </div>
    </div>
  );
}